// =====================================================================
// PatientForm (PME Sprint 1 / S50) — formulario reusavel de paciente.
// 2 secoes: Identificacao + Anamnese base. Usado em create (/prontuario/novo)
// e edit (/prontuario/paciente/:id). Presentational: estado + validacao + UI;
// o pai cuida de API (create/update/delete) + navegacao.
//
// Contrato com o backend (whitelist PatientService.WRITABLE_FIELDS):
//   fullName,cpf,rg,birthDate,gender,phone,email,address(JSONB),
//   allergies,medicationsCurrent,familyHistory,personalHistory,
//   habits(JSONB),notes  (companyId/createdBy vem do JWT, nunca do body)
// =====================================================================
import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Button,
  Divider,
  CircularProgress
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { toast } from "react-toastify";

import PatientPhoto from "./PatientPhoto";

const GENDER_OPTIONS = ["Masculino", "Feminino", "Outro", "Prefiro não informar"];
const SMOKING_OPTIONS = ["Não fuma", "Fumante", "Ex-fumante"];
const ALCOHOL_OPTIONS = ["Não bebe", "Social", "Frequente", "Ex-etilista"];

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  sectionTitle: {
    marginBottom: theme.spacing(1.5),
    fontWeight: 600
  },
  photoRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap"
  },
  actions: {
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "flex-end",
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  deleteBtn: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    marginRight: "auto"
  }
}));

// Mascara CPF conforme digita -> 000.000.000-00
const maskCpf = (v) => {
  const d = String(v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length > 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length > 6) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  if (d.length > 3) return `${d.slice(0, 3)}.${d.slice(3)}`;
  return d;
};

// Mascara telefone BR -> (00) 00000-0000
const maskPhone = (v) => {
  const d = String(v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

// patient (camelCase do backend) -> estado plano do form
const flatten = (p) => {
  const a = (p && p.address) || {};
  const h = (p && p.habits) || {};
  return {
    fullName: (p && p.fullName) || "",
    cpf: maskCpf((p && p.cpf) || ""),
    rg: (p && p.rg) || "",
    birthDate: (p && p.birthDate) ? String(p.birthDate).slice(0, 10) : "",
    gender: (p && p.gender) || "",
    phone: maskPhone((p && p.phone) || ""),
    email: (p && p.email) || "",
    cep: a.cep || "",
    logradouro: a.logradouro || "",
    numero: a.numero || "",
    complemento: a.complemento || "",
    bairro: a.bairro || "",
    cidade: a.cidade || "",
    uf: a.uf || "",
    allergies: (p && p.allergies) || "",
    medicationsCurrent: (p && p.medicationsCurrent) || "",
    familyHistory: (p && p.familyHistory) || "",
    personalHistory: (p && p.personalHistory) || "",
    habitSmoking: h.smoking || "",
    habitAlcohol: h.alcohol || "",
    habitExercise: h.exercise || "",
    habitSleep: h.sleep || "",
    notes: (p && p.notes) || ""
  };
};

// estado plano -> payload pro backend
const buildPayload = (f) => {
  const addr = {
    cep: f.cep,
    logradouro: f.logradouro,
    numero: f.numero,
    complemento: f.complemento,
    bairro: f.bairro,
    cidade: f.cidade,
    uf: f.uf
  };
  const hasAddr = Object.values(addr).some((v) => v && String(v).trim() !== "");
  const habits = {
    smoking: f.habitSmoking,
    alcohol: f.habitAlcohol,
    exercise: f.habitExercise,
    sleep: f.habitSleep
  };
  const hasHabits = Object.values(habits).some((v) => v && String(v).trim() !== "");
  return {
    fullName: f.fullName,
    cpf: f.cpf, // backend normaliza pra dígitos (S50)
    rg: f.rg,
    birthDate: f.birthDate ? f.birthDate : null, // DATEONLY: "" inválido -> null
    gender: f.gender,
    phone: f.phone,
    email: f.email,
    address: hasAddr ? addr : null,
    allergies: f.allergies,
    medicationsCurrent: f.medicationsCurrent,
    familyHistory: f.familyHistory,
    personalHistory: f.personalHistory,
    habits: hasHabits ? habits : null,
    notes: f.notes
  };
};

const PatientForm = ({ mode = "create", initialPatient = null, saving = false, onSave, onCancel, onDelete, onPhotoChange }) => {
  const classes = useStyles();
  const [form, setForm] = useState(() => flatten(initialPatient));
  const [nameError, setNameError] = useState(false);

  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "fullName" && value && value.trim()) setNameError(false);
  };
  // S53: capturar+mascarar ANTES do updater (pooling de eventos do React).
  const setCpf = (e) => {
    const v = maskCpf(e.target.value);
    setForm((prev) => ({ ...prev, cpf: v }));
  };
  const setPhone = (e) => {
    const v = maskPhone(e.target.value);
    setForm((prev) => ({ ...prev, phone: v }));
  };

  const cpfDigits = form.cpf.replace(/\D/g, "");
  const cpfHint = cpfDigits.length > 0 && cpfDigits.length !== 11 ? "CPF incompleto (11 dígitos)" : " ";

  const handleSubmit = () => {
    if (!form.fullName || !form.fullName.trim()) {
      setNameError(true);
      toast.error("Informe o nome do paciente.");
      return;
    }
    onSave(buildPayload(form));
  };

  return (
    <>
      {/* ---------- Seção 1: Identificação ---------- */}
      <Paper className={classes.section} variant="outlined">
        <Typography variant="h6" className={classes.sectionTitle}>
          Identificação
        </Typography>

        {/* Foto do paciente (PME Sprint 3a). No cadastro novo só aparece após
            salvar (precisa do id); na edição é editável (trocar/remover). */}
        <div className={classes.photoRow}>
          <PatientPhoto
            patientId={initialPatient && initialPatient.id}
            photoUrl={initialPatient && initialPatient.photoUrl}
            size={104}
            editable={mode === "edit" && !!(initialPatient && initialPatient.id)}
            onChange={onPhotoChange}
          />
          <Typography variant="body2" color="textSecondary">
            {mode === "edit"
              ? "Clique na câmera para adicionar ou trocar a foto. PNG, JPG ou WEBP, até 5 MB."
              : "A foto do paciente pode ser adicionada após salvar o cadastro."}
          </Typography>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nome completo"
              value={form.fullName}
              onChange={set("fullName")}
              required
              error={nameError}
              helperText={nameError ? "Nome é obrigatório" : " "}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="CPF"
              value={form.cpf}
              onChange={setCpf}
              placeholder="000.000.000-00"
              helperText={cpfHint}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="RG"
              value={form.rg}
              onChange={set("rg")}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Data de nascimento"
              type="date"
              value={form.birthDate}
              onChange={set("birthDate")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label="Gênero"
              value={form.gender}
              onChange={set("gender")}
              fullWidth
              variant="outlined"
              size="small"
            >
              <MenuItem value="">—</MenuItem>
              {GENDER_OPTIONS.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Telefone"
              value={form.phone}
              onChange={setPhone}
              placeholder="(00) 00000-0000"
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="E-mail"
              type="email"
              value={form.email}
              onChange={set("email")}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Endereço (-> address JSONB) */}
          <Grid item xs={12} sm={3}>
            <TextField label="CEP" value={form.cep} onChange={set("cep")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Logradouro" value={form.logradouro} onChange={set("logradouro")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Número" value={form.numero} onChange={set("numero")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Complemento" value={form.complemento} onChange={set("complemento")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Bairro" value={form.bairro} onChange={set("bairro")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Cidade" value={form.cidade} onChange={set("cidade")} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={6} sm={1}>
            <TextField label="UF" value={form.uf} onChange={set("uf")} inputProps={{ maxLength: 2 }} fullWidth variant="outlined" size="small" />
          </Grid>
        </Grid>
      </Paper>

      {/* ---------- Seção 2: Anamnese base ---------- */}
      <Paper className={classes.section} variant="outlined">
        <Typography variant="h6" className={classes.sectionTitle}>
          Anamnese base
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Alergias" value={form.allergies} onChange={set("allergies")} multiline minRows={2} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Medicações em uso" value={form.medicationsCurrent} onChange={set("medicationsCurrent")} multiline minRows={2} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Histórico familiar" value={form.familyHistory} onChange={set("familyHistory")} multiline minRows={2} fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Histórico pessoal" value={form.personalHistory} onChange={set("personalHistory")} multiline minRows={2} fullWidth variant="outlined" size="small" />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">
              Hábitos
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select label="Tabagismo" value={form.habitSmoking} onChange={set("habitSmoking")} fullWidth variant="outlined" size="small">
              <MenuItem value="">—</MenuItem>
              {SMOKING_OPTIONS.map((o) => (
                <MenuItem key={o} value={o}>{o}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select label="Álcool" value={form.habitAlcohol} onChange={set("habitAlcohol")} fullWidth variant="outlined" size="small">
              <MenuItem value="">—</MenuItem>
              {ALCOHOL_OPTIONS.map((o) => (
                <MenuItem key={o} value={o}>{o}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Exercício" value={form.habitExercise} onChange={set("habitExercise")} placeholder="ex: 3x/semana" fullWidth variant="outlined" size="small" />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Sono" value={form.habitSleep} onChange={set("habitSleep")} placeholder="ex: 7h/noite" fullWidth variant="outlined" size="small" />
          </Grid>

          <Grid item xs={12}>
            <TextField label="Observações" value={form.notes} onChange={set("notes")} multiline minRows={2} fullWidth variant="outlined" size="small" />
          </Grid>
        </Grid>
      </Paper>

      {/* ---------- Ações ---------- */}
      <div className={classes.actions}>
        {mode === "edit" && onDelete && (
          <Button variant="outlined" className={classes.deleteBtn} startIcon={<DeleteOutlineIcon />} onClick={onDelete} disabled={saving}>
            Excluir
          </Button>
        )}
        <Button onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </>
  );
};

export default PatientForm;
